/* Noemi Flowers — product page helpers: delivery date/time picker, add-on buttons,
   required line-item property validation before Dawn's product-form submits. */
(() => {
  const pad = (n) => String(n).padStart(2, '0');
  const isoDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  /* Parses "9:00 AM - 12:00 PM" / "14:00 - 17:00" → end time in minutes (last time in the string). */
  const slotEndMinutes = (text) => {
    const matches = [...String(text).matchAll(/(\d{1,2})(?::(\d{2}))?\s*(a\.?\s?m\.?|p\.?\s?m\.?)?/gi)];
    if (!matches.length) return null;
    const m = matches[matches.length - 1];
    let h = parseInt(m[1], 10);
    const min = parseInt(m[2] || '0', 10);
    const ap = (m[3] || '').toLowerCase().replace(/[\s.]/g, '');
    if (ap === 'pm' && h < 12) h += 12;
    if (ap === 'am' && h === 12) h = 0;
    if (h > 24) return null;
    return h * 60 + min;
  };

  if (!customElements.get('nf-delivery')) {
    customElements.define(
      'nf-delivery',
      class NfDelivery extends HTMLElement {
        connectedCallback() {
          this.dateInput = this.querySelector('[data-nf-date]');
          this.timeSelect = this.querySelector('[data-nf-time]');
          this.error = this.querySelector('.nf-pdp-delivery__error');
          if (!this.dateInput) return;

          const now = new Date();
          const cutoff = parseInt(this.dataset.cutoff || '0', 10);
          const min = new Date(now);
          if (cutoff > 0 && now.getHours() >= cutoff) min.setDate(min.getDate() + 1);
          this.dateInput.min = isoDate(min);
          const maxDays = parseInt(this.dataset.maxDays || '0', 10);
          if (maxDays > 0) {
            const max = new Date(now);
            max.setDate(max.getDate() + maxDays);
            this.dateInput.max = isoDate(max);
          }

          const field = this.dateInput.closest('.nf-pdp-field');
          this.dateValue = this.querySelector('[data-nf-date-value]');
          const sync = () => {
            field.classList.toggle('is-filled', !!this.dateInput.value);
            if (this.dateValue) {
              this.dateValue.value = this.formatDate(this.dateInput.value);
              this.dateValue.disabled = !this.dateValue.value;
            }
            this.updateSlots();
            this.clearError();
          };
          this.dateInput.addEventListener('change', sync);
          this.dateInput.addEventListener('input', sync);
          field.addEventListener('click', (e) => {
            if (e.target !== this.dateInput && typeof this.dateInput.showPicker === 'function') {
              try { this.dateInput.showPicker(); } catch (err) { this.dateInput.focus(); }
            }
          });
          this.timeSelect?.addEventListener('change', () => {
            this.timeSelect.closest('.nf-pdp-field').classList.toggle('is-filled', !!this.timeSelect.value);
            this.clearError();
          });
          sync();
        }

        /* "2026-10-09" → "viernes, 9 de octubre de 2026" (falls back to dd/mm/yyyy) */
        formatDate(iso) {
          if (!iso) return '';
          const [y, m, d] = iso.split('-').map(Number);
          if (!y || !m || !d) return iso;
          try {
            return new Date(y, m - 1, d, 12).toLocaleDateString('es-PA', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            });
          } catch (e) {
            return `${pad(d)}/${pad(m)}/${y}`;
          }
        }

        updateSlots() {
          if (!this.timeSelect) return;
          const today = isoDate(new Date());
          const isToday = this.dateInput.value === today;
          const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
          Array.from(this.timeSelect.options).forEach((opt) => {
            if (!opt.value) return;
            const end = slotEndMinutes(opt.value);
            const past = isToday && end !== null && end <= nowMin;
            opt.disabled = past;
            if (past && opt.selected) this.timeSelect.value = '';
          });
        }

        validate() {
          const invalid = [this.dateInput, this.timeSelect].filter(
            (el) => el && el.required && !el.value
          );
          if (this.dateInput?.value && this.dateInput.min && this.dateInput.value < this.dateInput.min) {
            invalid.unshift(this.dateInput);
          }
          if (!invalid.length) return true;
          invalid.forEach((el) => el.closest('.nf-pdp-field')?.classList.add('is-invalid'));
          if (this.error) this.error.hidden = false;
          this.scrollIntoView({ behavior: 'smooth', block: 'center' });
          invalid[0].focus({ preventScroll: true });
          return false;
        }

        clearError() {
          this.querySelectorAll('.is-invalid').forEach((el) => el.classList.remove('is-invalid'));
          if (this.error) this.error.hidden = true;
        }
      }
    );
  }

  /* Block submit of the main product form while required delivery fields / properties are empty.
     Runs in capture phase so it executes before Dawn's product-form submit handler. */
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.getAttribute('data-type') !== 'add-to-cart-form') return;
      /* note: form.id is shadowed by the <input name="id"> inside Dawn's product form */
      const formId = form.getAttribute('id');
      if (!formId) return;
      const delivery = document.querySelector(`nf-delivery [form="${formId}"]`)?.closest('nf-delivery');
      let ok = delivery ? delivery.validate() : true;
      if (ok) {
        const others = Array.from(document.querySelectorAll(`textarea[form="${formId}"][required], input[form="${formId}"][required]`)).filter(
          (el) => !el.closest('nf-delivery') && !el.value.trim()
        );
        if (others.length) {
          ok = false;
          others[0].reportValidity();
        }
      }
      if (!ok) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );

  if (!customElements.get('nf-addon-button')) {
    customElements.define(
      'nf-addon-button',
      class NfAddonButton extends HTMLElement {
        connectedCallback() {
          this.button = this.querySelector('button');
          this.button?.addEventListener('click', this.onClick.bind(this));
        }

        async onClick() {
          if (this.button.getAttribute('aria-disabled') === 'true') return;
          this.button.setAttribute('aria-disabled', 'true');
          this.classList.add('is-loading');
          const wrapper = this.closest('.nf-pdp-addons');
          const errorEl = wrapper?.querySelector('.nf-pdp-addons__error');
          if (errorEl) errorEl.hidden = true;

          const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
          const body = {
            items: [{ id: Number(this.dataset.variantId), quantity: 1 }],
          };
          if (cart && typeof cart.getSectionsToRender === 'function') {
            body.sections = cart.getSectionsToRender().map((s) => s.id);
            body.sections_url = window.location.pathname;
          }

          try {
            const res = await fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify(body),
            });
            const data = await res.json();
            if (!res.ok || data.status) throw new Error(data.description || data.message || 'Error');

            this.markAdded();
            const item = data.items ? data.items[0] : data;
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'nf-addon',
                productVariantId: this.dataset.variantId,
                cartData: data,
              });
            }
            if (cart && data.sections) {
              const state = Object.assign({}, item, { sections: data.sections });
              if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
              cart.renderContents(state);
            } else if (!cart) {
              window.location.href = (window.routes && window.routes.cart_url) || '/cart';
            }
          } catch (err) {
            if (errorEl) {
              errorEl.textContent = err.message;
              errorEl.hidden = false;
            }
          } finally {
            this.button.removeAttribute('aria-disabled');
            this.classList.remove('is-loading');
          }
        }

        markAdded() {
          const text = this.querySelector('.nf-pdp-addon__link-text');
          const done = this.querySelector('.nf-pdp-addon__link-done');
          if (!done || !done.textContent.trim()) return;
          text.hidden = true;
          done.hidden = false;
          clearTimeout(this.resetTimer);
          this.resetTimer = setTimeout(() => {
            text.hidden = false;
            done.hidden = true;
          }, 4000);
        }
      }
    );
  }
})();
